/**
 * APX WorkCenter — Bookkeeper Dashboard
 * Design: "Gold Standard" — Premium Editorial Finance
 *
 * Shows only clients where the logged-in bookkeeper has at least one T1/T2/T3 tier assigned.
 * Each client row displays the bookkeeper's specific tiers with individual status dropdowns.
 * T3-C (P&L Done) is Jany's exclusive tier — never shown here.
 */

import { useState, useMemo, useEffect } from "react";
import { Link } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { usePreview } from "@/contexts/PreviewContext";
import { trpc } from "@/lib/trpc";
import AllCommunicationsFeed from "@/components/AllCommunicationsFeed";
import SmsCombinedPanel from "@/components/SmsCombinedPanel";
import BookkeeperHistoryPanel from "@/components/BookkeeperHistoryPanel";
import ClientDocuments from "@/components/ClientDocuments";
import { toast } from "sonner";
import FileDropZone from "@/components/FileDropZone";
import { ContactClientModal } from "@/components/ContactClientModal";
import SendNotificationModal from "@/components/SendNotificationModal";
import ReconNotesPanel from "@/components/ReconNotesPanel";

// Bookkeepers available for preview (MD only)
const PREVIEW_BOOKKEEPERS = [
  { name: "Irit P.",    email: "iritna@gmail.com" },
  { name: "Yunuen A.", email: "yunuenalfaro7@gmail.com" },
  { name: "Shobhit A.", email: "shobhit@advancefinserv.com" },
  { name: "Lulu M.",   email: "lourdesmastache20@gmail.com" },
  { name: "Marian H.", email: "marianjoyhernandez2@gmail.com" },
  { name: "Renata L.", email: "erenatalazo@gmail.com" },
  { name: "Arvind K.",  email: "arvind@advancefinserv.com" },
];

type TierStatus = "not_started" | "in_progress" | "complete" | "overdue";

const STATUS_OPTIONS: Array<{ value: TierStatus; label: string; color: string }> = [
  { value: "not_started", label: "Not Started", color: "oklch(0.45 0.02 240)" },
  { value: "in_progress", label: "In Progress",  color: "oklch(0.55 0.16 85)" },  // yellow (overridden per-tier)
  { value: "complete",    label: "Complete",      color: "oklch(0.40 0.15 145)" }, // green
  { value: "overdue",     label: "Overdue",       color: "oklch(0.50 0.20 25)" },  // red-orange
];

const TIER_META: Record<string, { label: string; code: string; color: string; description: string }> = {
  T1: { label: "Bank Feed",             code: "B",   color: "oklch(0.55 0.16 85)",  description: "Connect & verify the bank/credit card feed so all transactions flow in correctly" },
  T2: { label: "Coding",                code: "C",   color: "oklch(0.45 0.18 240)", description: "Categorize & code every transaction to the correct account and class" },
  T3: { label: "Anomalies / Trial Bal.", code: "ATB", color: "oklch(0.45 0.20 300)", description: "Review flagged anomalies, reconcile accounts, and confirm the Trial Balance is clean" },
};

export default function BookkeeperDashboard() {
  const { user } = useAuth();
  const { isPreviewMode, exitPreview } = usePreview();
  const [showFiles, setShowFiles] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [expandedDocs, setExpandedDocs] = useState<string | null>(null);
  const [expandedRecon, setExpandedRecon] = useState<string | null>(null);
  // When in preview mode, BKKPR Manager can pick which bookkeeper's view to see
  // Initialize from sessionStorage if set by the Manager's "View as Bkkpr" picker
  const [previewBkkprEmail, setPreviewBkkprEmail] = useState(() => {
    const stored = sessionStorage.getItem("previewBookkeeperEmail");
    if (stored && PREVIEW_BOOKKEEPERS.some(b => b.email === stored)) return stored;
    return PREVIEW_BOOKKEEPERS[3].email; // default: Lulu
  });

  // Sync when sessionStorage changes (e.g., user picks a different bookkeeper from the manager view)
  useEffect(() => {
    const stored = sessionStorage.getItem("previewBookkeeperEmail");
    if (stored && PREVIEW_BOOKKEEPERS.some(b => b.email === stored)) {
      setPreviewBkkprEmail(stored);
    }
  }, [isPreviewMode]);

  const utils = trpc.useUtils();

  // In preview mode use the selected bookkeeper's email; otherwise use the logged-in user's email
  const queryEmail = isPreviewMode ? previewBkkprEmail : (user?.email ?? "");
  const previewBkkprName = PREVIEW_BOOKKEEPERS.find(b => b.email === previewBkkprEmail)?.name ?? "";

  // Pull all client statuses to access notes per client
  const { data: allClientStatuses = [] } = trpc.clientStatus.listAll.useQuery(undefined, { refetchInterval: 5000 });
  const clientNotesMap = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const c of allClientStatuses) map[c.clientName] = c.notes ?? null;
    return map;
  }, [allClientStatuses]);

  // Pull ALL tier tasks assigned to this bookkeeper (by email) — 5s polling for real-time team sync
  const { data: myTierTasks = [], isLoading: tierLoading } = trpc.tierTasks.list.useQuery(
    { assigneeEmail: queryEmail },
    { enabled: !!queryEmail, refetchInterval: 5000 }
  );

  const setIndividualDueDate = trpc.tierTasks.setDueDate.useMutation({
    onMutate: async (vars) => {
      await utils.tierTasks.list.cancel({ assigneeEmail: queryEmail });
      const prev = utils.tierTasks.list.getData({ assigneeEmail: queryEmail });
      utils.tierTasks.list.setData({ assigneeEmail: queryEmail }, old =>
        old?.map(t => t.id === vars.id ? { ...t, dueAt: vars.dueAt } : t)
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.tierTasks.list.setData({ assigneeEmail: queryEmail }, ctx.prev);
      toast.error("Failed to update due date");
    },
    onSettled: () => utils.tierTasks.list.invalidate({ assigneeEmail: queryEmail }),
  });

  const updateTierStatus = trpc.tierTasks.updateStatus.useMutation({
    // Optimistic update: immediately reflect status change in UI before server confirms
    onMutate: async (vars) => {
      await utils.tierTasks.list.cancel({ assigneeEmail: queryEmail });
      const prev = utils.tierTasks.list.getData({ assigneeEmail: queryEmail });
      utils.tierTasks.list.setData({ assigneeEmail: queryEmail }, old =>
        old?.map(t => t.id === vars.id ? { ...t, status: vars.status } : t)
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) utils.tierTasks.list.setData({ assigneeEmail: queryEmail }, ctx.prev);
      toast.error("Failed to save status", { description: err.message });
    },
    onSettled: () => utils.tierTasks.list.invalidate({ assigneeEmail: queryEmail }),
  });

  // Group tier tasks by clientName
  const clientsMap = useMemo(() => {
    const map: Record<string, typeof myTierTasks> = {};
    for (const task of myTierTasks) {
      if (!map[task.clientName]) map[task.clientName] = [];
      map[task.clientName].push(task);
    }
    return map;
  }, [myTierTasks]);

  const clientNames = useMemo(() => Object.keys(clientsMap).sort(), [clientsMap]);

  // ── Client search ──────────────────────────────────────────────────────────
  const [clientSearch, setClientSearch] = useState("");
  const [contactModal, setContactModal] = useState<{ clientName: string; clientEmail?: string | null; clientPhone?: string | null } | null>(null);
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});
  const updateClientStatus = trpc.clientStatus.update.useMutation({
    onSuccess: (_result, variables) => {
      setEditingNotes(previous => {
        const next = { ...previous };
        delete next[variables.clientName];
        return next;
      });
      utils.clientStatus.listAll.invalidate();
      toast.success("Note saved");
    },
    onError: (error, variables) => {
      setEditingNotes(previous => {
        const next = { ...previous };
        delete next[variables.clientName];
        return next;
      });
      toast.error("Failed to save note", { description: error.message });
    },
  });
  const filteredClientNames = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clientNames;
    return clientNames.filter((name) => name.toLowerCase().includes(q));
  }, [clientNames, clientSearch]);

  // Summary stats
  const allMyTasks = myTierTasks;
  const complete = allMyTasks.filter(t => t.status === "complete").length;
  const overdue = allMyTasks.filter(t => t.status === "overdue").length;
  const inProgress = allMyTasks.filter(t => t.status === "in_progress").length;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <DashboardLayout previewEmail={previewBkkprEmail}>
      <div className="space-y-6 animate-fade-slide-up">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="apx-phase-badge">Bkkpr</span>
            </div>
            <h1 className="text-2xl font-bold text-[oklch(0.12_0.04_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {getGreeting()}, {user?.name}
            </h1>
            <p className="text-sm text-[oklch(0.08_0.01_240)] mt-0.5">
              {tierLoading
                ? "Loading your assignments…"
                : clientNames.length === 0
                ? "No tier tasks assigned yet — Jany will assign clients to you shortly."
                : `${clientNames.length} clients · ${complete} tasks complete${overdue > 0 ? ` · ${overdue} overdue` : ""}`}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap shrink-0">
            {/* Notify Client */}
            <button
              onClick={() => setContactModal({ clientName: "", clientEmail: null, clientPhone: null })}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Notify Client
            </button>
            {/* Notify Team */}
            <button
              onClick={() => setShowNotify(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Notify Team
            </button>
            {/* Send Files */}
            <button
              onClick={() => setShowFiles(v => !v)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {showFiles ? "Hide Files" : "Send Files"}
            </button>
            {/* View History — anchor link to Prior Month Completion History panel */}
            <a
              href="#history-panel"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.45 0.12 240 / 10%)", border: "2px solid oklch(0.45 0.12 240 / 50%)", color: "oklch(0.30 0.12 240)", textDecoration: "none" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              View History
            </a>
          </div>
        </div>

        {/* Preview As Bookkeeper selector — Director only, shown when in preview mode */}
        {isPreviewMode && (
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl"
            style={{ background: "oklch(0.92 0.005 240 / 8%)", border: "2px solid oklch(0.25 0.04 240)" }}>
            <svg className="w-4 h-4 shrink-0" style={{ color: "oklch(0.08 0.01 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="text-sm font-semibold" style={{ color: "oklch(0.08 0.01 240)" }}>Viewing as Bkkpr:</span>
            <select
              value={previewBkkprEmail}
              onChange={e => setPreviewBkkprEmail(e.target.value)}
              className="px-2 py-1 rounded text-sm font-semibold outline-none cursor-pointer"
              style={{ background: "oklch(0.92 0.005 240 / 15%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
              {PREVIEW_BOOKKEEPERS.map(bk => (
                <option key={bk.email} value={bk.email}>{bk.name}</option>
              ))}
            </select>
            <span className="text-sm text-[oklch(0.08_0.01_240)]">
              {clientNames.length} clients · {myTierTasks.length} tasks
            </span>
            <button
              onClick={() => { sessionStorage.removeItem("previewBookkeeperEmail"); sessionStorage.removeItem("previewBookkeeperName"); exitPreview(); }}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all"
              style={{
                background: "oklch(0.55 0.18 250 / 15%)",
                border: "1px solid oklch(0.55 0.18 250 / 40%)",
                color: "oklch(0.30 0.18 250)",
                cursor: "pointer",
              }}
            >
              ← Back to Bkkpr Mgr
            </button>
          </div>
        )}

        {/* File transfer panel */}
        {showFiles && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="apx-card rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[oklch(0.08_0.01_240)] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Send Files to Jany
              </h3>
              <FileDropZone defaultRecipient="email:lopez.jany@gmail.com" />
            </div>

          </div>
        )}

        {/* Summary stats */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          {[
            { label: "Clients",     value: tierLoading ? "—" : clientNames.length.toString(), color: "oklch(0.08 0.01 240)" },
            { label: "In Progress", value: tierLoading ? "—" : inProgress.toString(),          color: "oklch(0.08 0.01 240)" },
            { label: "Complete",    value: tierLoading ? "—" : complete.toString(),             color: "oklch(0.08 0.01 240)" },
            { label: "Overdue",     value: tierLoading ? "—" : overdue.toString(),              color: overdue > 0 ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)" },
          ].map((card) => (
            <div key={card.label} className="apx-card rounded-xl p-4">
              <p className="text-sm text-[oklch(0.08_0.01_240)] uppercase tracking-wider font-medium mb-2">{card.label}</p>
              <p className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: card.color }}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* ── Prior Month Completion History (self-view) ── */}
        <BookkeeperHistoryPanel
          filterEmail={isPreviewMode ? previewBkkprEmail : (user?.email ?? "")}
          title="My Prior Month Completion History"
          id="history-panel"
        />

        {/* ── Two-column body ── */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start">

          {/* LEFT: Client list with tier tasks */}
          <div className="space-y-5">
            <div className="apx-card rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-[oklch(0.08_0.01_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  My Schedule & Task Requests
                </h3>
                {/* Tier legend */}
                <div className="flex gap-2">
                  {Object.entries(TIER_META).map(([k, v]) => (
                    <span key={k} className="text-sm font-semibold px-1.5 py-0.5 rounded-full"
                      style={{ background: `${v.color}15`, color: v.color }}>
                      {k}={v.code}
                    </span>
                  ))}
                </div>
              </div>
              {/* Search bar */}
              {!tierLoading && clientNames.length > 0 && (
                <div className="relative mb-4">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                    style={{ color: "oklch(0.22 0.04 240)" }}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
                  </svg>
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder={`Search ${clientNames.length} clients…`}
                    className="w-full pl-9 pr-4 py-2 rounded-lg text-sm outline-none"
                    style={{
                      background: "oklch(0.93 0.008 240)",
                      border: "1px solid oklch(0.88 0.01 240)",
                      color: "oklch(0.20 0.02 240)",
                    }}
                  />
                  {clientSearch && (
                    <button
                      onClick={() => setClientSearch("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-sm"
                      style={{ color: "oklch(0.22 0.04 240)", background: "none", border: "none", cursor: "pointer" }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}

              {tierLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    
<div key={i} className="h-16 rounded-lg animate-pulse" style={{ background: "oklch(0.93 0.008 240)" }} />
                  ))}
                </div>
              ) : clientNames.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm text-[oklch(0.08_0.01_240)]">No tier tasks assigned to you yet.</p>
                  <p className="text-sm text-[oklch(0.30_0.03_240)] mt-1">Jany will assign T1/T2/T3 tasks from the Bookkeeping Manager dashboard.</p>
                </div>
              ) : filteredClientNames.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm text-[oklch(0.08_0.01_240)]">No clients match “{clientSearch}”.</p>
                  <button onClick={() => setClientSearch("")} className="text-sm mt-2" style={{ color: "oklch(0.08 0.01 240)", background: "none", border: "none", cursor: "pointer" }}>Clear search</button>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredClientNames.map((clientName) => {
                    const tasks = clientsMap[clientName];
                    const tasksByTier: Record<string, typeof tasks[0]> = {};
                    for (const t of tasks) tasksByTier[t.tier] = t;
                    const scheduledClient = allClientStatuses.find(c => c.clientName === clientName);
                    const isAnnualClient = scheduledClient?.isAnnual ?? false;
                    const isWorkCycleOverrideActive = scheduledClient?.workCycleOverrideActive ?? false;
                    const _bkMonth = new Date().getMonth();
                    const _bkBiAnnualActive = [0, 6, 11];
                    const isBiAnnualActive = isAnnualClient && (_bkBiAnnualActive.includes(_bkMonth) || isWorkCycleOverrideActive);
                    const isBiAnnualInactive = isAnnualClient && !_bkBiAnnualActive.includes(_bkMonth) && !isWorkCycleOverrideActive;

                    return (
                      <div key={clientName} className="space-y-0">
                        <div
                          className="px-3 py-3 rounded-lg"
                          style={{
                            background: isBiAnnualInactive ? "oklch(0.88 0.01 270 / 60%)" : isBiAnnualActive ? "oklch(0.90 0.02 270 / 40%)" : "oklch(0.93 0.008 240)",
                            borderRadius: expandedDocs === clientName ? "0.5rem 0.5rem 0 0" : undefined,
                            opacity: isBiAnnualInactive ? 0.75 : 1,
                          }}>
                          {/* Client name row */}
                          <div className="flex items-center gap-3 mb-2">
                            <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                              style={{ background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)" }}>
                              {clientName.charAt(0)}
                            </div>
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[oklch(0.18_0.04_240)] truncate">{clientName}</p>
                              {isAnnualClient && (
                                <span className="text-xs px-1.5 py-0.5 rounded-full shrink-0" style={{ background: isBiAnnualActive ? "oklch(0.35 0.14 145 / 15%)" : "oklch(0.35 0.08 270 / 15%)", color: isBiAnnualActive ? "oklch(0.30 0.14 145)" : "oklch(0.30 0.08 270)", border: isBiAnnualActive ? "1px solid oklch(0.35 0.14 145 / 30%)" : "1px solid oklch(0.35 0.08 270 / 30%)", fontSize: "10px", fontWeight: 600 }}>{isWorkCycleOverrideActive ? "Work Cycle · Active" : isBiAnnualActive ? "Scheduled · Active" : "Scheduled · Inactive"}</span>
                              )}
                              <input
                                value={editingNotes[clientName] ?? clientNotesMap[clientName] ?? ""}
                                onChange={e => setEditingNotes(prev => ({ ...prev, [clientName]: e.target.value }))}
                                onBlur={e => {
                                  const newNote = e.target.value.trim();
                                  if (newNote !== (clientNotesMap[clientName] ?? "")) {
                                    const clientRow = allClientStatuses.find(c => c.clientName === clientName);
                                    updateClientStatus.mutate({
                                      clientName,
                                      status: (clientRow?.status ?? "not_started") as "not_started" | "in_progress" | "complete" | "overdue",
                                      notes: newNote || undefined,
                                    });
                                  }
                                }}
                                placeholder="Note…"
                                className="text-sm text-[oklch(0.35_0.03_240)] bg-transparent outline-none flex-1 min-w-0 truncate placeholder:text-[oklch(0.55_0.02_240)] focus:text-[oklch(0.08_0.01_240)]"
                              />
                            </div>
                            {/* Contact Client button — hidden for Shobhit */}
                            {queryEmail !== "shobhit@advancefinserv.com" && (
                              <button
                                title="Contact Client"
                                className="px-2 py-1 rounded text-sm font-medium transition-all duration-150"
                                style={{ background: "oklch(0.94 0.018 220)", color: "oklch(0.08 0.01 240)", border: "2px solid oklch(0.25 0.04 240)" }}
                                onClick={(e) => { e.stopPropagation(); setContactModal({ clientName }); }}>
                                ✉️ Contact
                              </button>
                            )}
                            {/* Recon.Notes toggle */}
                            <button
                              className="px-2.5 py-1 rounded text-sm font-medium transition-all duration-150"
                              style={{
                                background: expandedRecon === clientName ? "oklch(0.45 0.14 75)" : "oklch(0.95 0.012 75 / 40%)",
                                color: expandedRecon === clientName ? "oklch(0.98 0.005 85)" : "oklch(0.30 0.08 75)",
                                border: expandedRecon === clientName ? "1px solid oklch(0.45 0.14 75)" : "1px solid oklch(0.72 0.14 75 / 40%)",
                              }}
                              onClick={() => {
                                setExpandedRecon(expandedRecon === clientName ? null : clientName);
                                if (expandedDocs === clientName) setExpandedDocs(null);
                              }}
                              title="Recon.Notes — add or view reconciliation work notes">
                              {expandedRecon === clientName ? "▲ Recon.Notes" : "▼ Recon.Notes"}
                            </button>
                            {/* Docs toggle */}
                            <button
                              className="px-2.5 py-1 rounded text-sm font-medium transition-all duration-150"
                              style={{
                                background: expandedDocs === clientName ? "oklch(0.92 0.005 240 / 20%)" : "oklch(0.97 0.005 85)",
                                color: expandedDocs === clientName ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)",
                                border: expandedDocs === clientName ? "1px solid oklch(0.92 0.005 240 / 50%)" : "1px solid oklch(0 0 0 / 10%)",
                              }}
                              onClick={() => {
                                setExpandedDocs(expandedDocs === clientName ? null : clientName);
                                if (expandedRecon === clientName) setExpandedRecon(null);
                              }}>
                              {expandedDocs === clientName ? "▲ Docs" : "▼ Docs"}
                            </button>
                          </div>

                          {/* Tier pills row */}
                          <div className="flex gap-2 flex-wrap pl-11">
                            {(["T1", "T2", "T3"] as const).map(tier => {
                              const task = tasksByTier[tier];
                              if (!task) return null;
                              // Bi-annual clients: greyed out when inactive (not Jul/Dec/Jan)
                              if (isBiAnnualInactive) {
                                return (
                                  <div key={tier} className="flex flex-col gap-0.5 px-2 py-1.5 rounded-lg" style={{ background: "oklch(0.35 0.08 270 / 8%)", border: "1px dashed oklch(0.35 0.08 270 / 30%)", opacity: 0.6 }} title="Bi-Annual client — tier work active in July, December & January">
                                    <span className="text-xs font-bold tracking-wide" style={{ color: "oklch(0.45 0.06 270)" }}>{tier} — Bi-Annual</span>
                                    <span className="text-xs" style={{ color: "oklch(0.50 0.04 270)" }}>Active: Jul / Dec / Jan</span>
                                  </div>
                                );
                              }
                              const meta = TIER_META[tier];
                              // Use tier-specific color for in_progress: T1=yellow, T2=blue, T3=purple
                              const baseStatusOpt = STATUS_OPTIONS.find(s => s.value === task.status) ?? STATUS_OPTIONS[0];
                              const statusOpt = task.status === "in_progress"
                                ? { ...baseStatusOpt, color: meta.color }
                                : baseStatusOpt;
                              return (
                                <div key={tier} className="flex flex-col gap-0.5 px-2 py-1.5 rounded-lg"
                                  style={{ background: `${meta.color}10`, border: `1px solid ${meta.color}20` }}>
                                  {/* Tier label */}
                                  <span className="text-xs font-bold tracking-wide" style={{ color: meta.color }}>
                                    {tier} — {meta.label}
                                  </span>
                                  {/* Status dropdown */}
                                  <div className="relative">
                                    <select
                                      value={task.status}
                                      onChange={(e) => updateTierStatus.mutate({
                                        id: task.id,
                                        status: e.target.value as TierStatus,
                                        callerEmail: user?.email ?? "",
                                        callerName: isPreviewMode ? previewBkkprName : (user?.name ?? undefined),
                                      })}
                                      className="appearance-none pl-1.5 pr-5 py-0.5 rounded text-sm font-semibold cursor-pointer outline-none"
                                      style={{
                                        background: `${statusOpt.color}15`,
                                        color: statusOpt.color,
                                        border: `1px solid ${statusOpt.color}35`,
                                      }}>
                                      {STATUS_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                    <svg className="absolute right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 pointer-events-none"
                                      style={{ color: statusOpt.color }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                  {/* Completed timestamp — shown when status is complete */}
                                  {task.status === "complete" && task.completedAt && (
                                    <div
                                      className="text-[10px] px-1 py-0.5 rounded"
                                      style={{ background: "oklch(0.40 0.15 145 / 10%)", color: "oklch(0.30 0.12 145)", border: "1px solid oklch(0.40 0.15 145 / 30%)", whiteSpace: "nowrap" }}
                                      title={`Completed: ${new Date(task.completedAt).toLocaleString()}`}
                                    >
                                      ✓ {new Date(task.completedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}{" "}
                                      <span style={{ opacity: 0.75 }}>{new Date(task.completedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                                    </div>
                                  )}
                                  {/* Due date — editable for upper management only; read-only for bookkeepers */}
                                  {(() => {
                                    const dueEndOfDay = task.dueAt ? task.dueAt + (24 * 60 * 60 * 1000 - 1) : null;
                                    const isOverdue = dueEndOfDay !== null && dueEndOfDay < Date.now() && task.status !== "complete";
                                    const currentVal = task.dueAt
                                      ? new Date(task.dueAt).toISOString().split("T")[0]
                                      : "";
                                    const canEditDueDate = isPreviewMode || ["super_admin", "admin", "controller", "asst_controller", "bookkeeping_manager", "bookkeeper_supervisor"].includes(user?.role ?? "");
                                    const dateStyle = {
                                      background: isOverdue
                                        ? "oklch(0.55 0.18 25 / 15%)"
                                        : task.dueAt
                                        ? "oklch(0.72 0.14 75 / 18%)"
                                        : "oklch(0.92 0.005 240 / 10%)",
                                      border: `1px solid ${isOverdue ? "oklch(0.55 0.18 25 / 40%)" : task.dueAt ? "oklch(0.72 0.14 75 / 40%)" : "oklch(0.5 0.02 240 / 20%)"}`,
                                      color: isOverdue
                                        ? "oklch(0.55 0.18 25)"
                                        : task.dueAt
                                        ? "oklch(0.45 0.10 75)"
                                        : "oklch(0.45 0.02 240)",
                                      colorScheme: "light" as const,
                                    };
                                    if (!canEditDueDate) {
                                      // Read-only display for bookkeepers
                                      return (
                                        <div
                                          className="text-xs rounded px-1 py-0.5"
                                          style={{ ...dateStyle, cursor: "default" }}
                                          title={task.dueAt ? (isOverdue ? "Overdue" : "Due date") : "No due date set"}
                                        >
                                          {currentVal || <span style={{ opacity: 0.5 }}>mm/dd/yyyy</span>}
                                        </div>
                                      );
                                    }
                                    return (
                                      <div className="relative group/due flex items-center gap-0.5">
                                        <input
                                          type="date"
                                          defaultValue={currentVal}
                                          key={currentVal}
                                          onChange={e => {
                                            const val = e.target.value;
                                            const ts = val ? new Date(val + "T00:00:00").getTime() : null;
                                            setIndividualDueDate.mutate({ id: task.id, dueAt: ts, callerEmail: user?.email ?? "" });
                                          }}
                                          className="text-xs rounded px-1 py-0.5 outline-none w-full cursor-pointer"
                                          style={dateStyle}
                                          title={task.dueAt
                                            ? (isOverdue ? "Overdue — click to change date" : "Individual due date — click to change")
                                            : "Set individual due date for this tier"}
                                        />
                                        {task.dueAt && (
                                          <button
                                            onClick={() => setIndividualDueDate.mutate({ id: task.id, dueAt: null, callerEmail: user?.email ?? "" })}
                                            className="opacity-0 group-hover/due:opacity-100 transition-opacity shrink-0"
                                            title="Clear this due date"
                                            style={{ color: "oklch(0.45 0.02 240)", fontSize: "10px", lineHeight: 1 }}
                                          >✕</button>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Recon.Notes inline panel */}
                        <ReconNotesPanel
                          clientId={clientName}
                          clientName={clientName}
                          isOpen={expandedRecon === clientName}
                          onClose={() => setExpandedRecon(null)}
                        />
                        {/* Expandable Drive documents panel */}
                        {expandedDocs === clientName && (
                          <div className="rounded-b-lg overflow-hidden"
                            style={{ border: "1px solid oklch(0.82 0.02 240)", background: "oklch(0.97 0.005 85)", borderTop: "none" }}>
                            <div className="px-3 py-2 flex items-center gap-2"
                              style={{ borderBottom: "1px solid oklch(0.88 0.01 240)", background: "oklch(0.93 0.008 240)" }}>
                              <svg className="w-3.5 h-3.5" style={{ color: "oklch(0.08 0.01 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 15l4.5-8h9L21 15M3 15h18M3 15l2 4h14l2-4" />
                              </svg>
                              <span className="text-sm font-semibold" style={{ color: "oklch(0.08 0.01 240)" }}>Drive Documents — {clientName}</span>
                              <button onClick={() => setExpandedDocs(null)}
                                className="ml-auto text-[oklch(0.30_0.03_240)] hover:text-[oklch(0.08_0.01_240)] transition-colors">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                            <div className="p-3"><ClientDocuments clientName={clientName} /></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Xero Connected Notice */}
            <div className="rounded-xl p-4 flex items-start gap-3"
              style={{ background: "oklch(0.92 0.005 240 / 8%)", border: "2px solid oklch(0.25 0.04 240)" }}>
              <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "oklch(0.08 0.01 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold" style={{ color: "oklch(0.08 0.01 240)" }}>APX's Xero account is connected</p>
              </div>
            </div>
          </div>{/* end left column */}

          {/* RIGHT: Schedule + Inbox — sticky */}
          <div className="xl:sticky xl:top-6 space-y-5">

          </div>{/* end right column */}

        </div>{/* end two-column grid */}

        {/* ── BOTTOM: SMS (left) + All Communications (right) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-5 items-stretch">
          <div className="apx-card rounded-xl overflow-hidden flex flex-col">
            <SmsCombinedPanel />
          </div>
          <div className="apx-card rounded-xl overflow-hidden p-4" style={{ minHeight: 420 }}>
            <AllCommunicationsFeed
              email={isPreviewMode ? previewBkkprEmail : (user?.email ?? "")}
              role={user?.role ?? "bookkeeper"}
              userName={isPreviewMode ? previewBkkprName : (user?.name ?? "Bkkpr")}
              userRole={user?.role ?? "bookkeeper"}
            />
          </div>
        </div>

      </div>

      {showNotify && <SendNotificationModal onClose={() => setShowNotify(false)} />}

      {/* Contact Client Modal */}
      {contactModal && (
        <ContactClientModal
          open={!!contactModal}
          onClose={() => setContactModal(null)}
          clientName={contactModal.clientName}
          clientEmail={contactModal.clientEmail}
          clientPhone={contactModal.clientPhone}
          senderName={isPreviewMode ? previewBkkprName : (user?.name ?? "APX Team")}
          senderEmail={isPreviewMode ? previewBkkprEmail : (user?.email ?? "")}
          userRole={user?.role ?? "bookkeeper"}
        />
      )}
    </DashboardLayout>
  );
}
